import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signInUser, resetPassword } from '../../store/slices/authSlice';
import './Login.scss';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redirect if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Toggle forgot password mode
  const toggleForgotPassword = () => {
    setIsForgotPasswordMode(!isForgotPasswordMode);
    setValidationError('');
    setSuccessMessage('');
  };

  // Go back to login mode
  const backToLogin = () => {
    setIsForgotPasswordMode(false);
    setValidationError('');
    setSuccessMessage('');
  };

  // Validate form data
  const validateForm = () => {
    setValidationError('');
    
    if (!email.trim()) {
      setValidationError('Email is required');
      return false;
    }
    
    // Only validate password for login and signup modes
    if (!isForgotPasswordMode) {
      if (!password.trim()) {
        setValidationError('Password is required');
        return false;
      }
      
      if (password.length < 6) {
        setValidationError('Password should be at least 6 characters long');
        return false;
      }
    }
    
    return true;
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    const normalizedEmail = email.trim().toLowerCase();
    
    if (!normalizedEmail) {
      setValidationError('Email is required');
      return;
    }
    
    try {
      const resultAction = await dispatch(resetPassword({ email: normalizedEmail }));
      if (resetPassword.fulfilled.match(resultAction)) {
        setSuccessMessage('Password reset email sent! Please check your inbox and follow the instructions to reset your password.');
        setValidationError('');
      }
    } catch (error) {
      console.error('Password reset error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Normalize email to lowercase
    const normalizedEmail = email.trim().toLowerCase();
    setEmail(normalizedEmail);
    
    // Handle forgot password submission
    if (isForgotPasswordMode) {
      return handleForgotPassword(e);
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const resultAction = await dispatch(signInUser({ email: normalizedEmail, password }));
      if (signInUser.fulfilled.match(resultAction)) {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">MyelinZ PPC Portal</h1>
          <p className="login-subtitle">
            {isForgotPasswordMode 
              ? 'Reset your password' 
              : 'Sign in to your account'
            }
          </p>
        </div>
        
        {(error || validationError) && (
          <div className="error-message">
            {validationError || error}
          </div>
        )}
        
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        
        {isForgotPasswordMode && (
          <div className="info-message">
            Enter your email address and we'll send you a link to reset your password.
          </div>
        )}
        
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              placeholder="Enter your email"
              required
            />
          </div>
          
          {!isForgotPasswordMode && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
          )}
          
          <div className="form-footer">
            {!isForgotPasswordMode && (
              <>
                <div className="remember-me">
                  <input type="checkbox" id="remember" />
                  <label htmlFor="remember">Remember me</label>
                </div>
                
                <button 
                  type="button" 
                  className="forgot-password"
                  onClick={toggleForgotPassword}
                >
                  Forgot password?
                </button>
              </>
            )}
            
            {isForgotPasswordMode && (
              <div className="forgot-password-footer">
                <button 
                  type="button" 
                  className="back-to-login"
                  onClick={backToLogin}
                >
                  ← Back to login
                </button>
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading 
              ? (isForgotPasswordMode 
                  ? 'Sending reset email...' 
                  : 'Signing in...'
                ) 
              : (isForgotPasswordMode 
                  ? 'Send Reset Email' 
                  : 'Sign in'
                )
            }
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login; 